import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from '@cyborg-monorepo/common/angular/cyborg-ui';
import {
  Enums,
  HuntPackageRecord,
  ThreatProfile,
  TitanRelatedReports,
} from '@cyborg-monorepo/hunter/common';
import { gatherTags } from '../../../../helpers/utils';
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service';
import { FeedbackModalComponent } from '../../../shared/components/dialogs/feedback-modal/feedback-modal.component';
import { FacetButtonItem } from '../../../shared/components/facet-buttons/facet-buttons.component';
import { FavoriteService } from '../../../shared/services/favorite.service';
import { HuntPackageService } from '../../../shared/services/hunt-package.service';
import { ThreatProfileService } from '../../../shared/services/threat-profile.service';
import { BreadcrumbsDataService } from '../../services/breadcrumbs.service';
import { TitanService } from '../../../shared/services/titan.service';
import { LoremIpsum } from 'lorem-ipsum';

@Component({
  standalone: false,
  templateUrl: './threat-profiles.component.html',
  styleUrls: ['./threat-profiles.component.scss'],
})
export class DrillDownThreatProfileComponent implements OnInit, OnDestroy {
  public relatedHuntPackages: Array<HuntPackageRecord>;
  public uuid: string;
  public favorited: boolean;
  public profile: ThreatProfile & {
    tags: Record<Enums.CyborgTagTypeEnum, string[]>;
  };
  public mitres: Array<{ technique: string; id: string }> = [];
  public tags: FacetButtonItem[];
  public titanRelatedReports: TitanRelatedReports;
  public lorem = new LoremIpsum();
  public loremTexts: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private favoriteService: FavoriteService,
    private bread: BreadcrumbsDataService,
    private trackingService: TrackingService,
    public dialog: DialogService,
    public threatProfileService: ThreatProfileService,
    public huntPackageService: HuntPackageService,
    public titanService: TitanService,
  ) {}

  public ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.uuid = params.uuid;
      this.getDetails();
      this.isFavorited();
      this.getTitanReports();
    });
  }

  public ngOnDestroy(): void {
    this.bread.resetCrumb();
  }

  public scrollTo(target: string): void {
    document.getElementById(target).scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }

  private getTitanReports(): void {
    this.titanService.getRelatedTitanReports(this.uuid).subscribe((reports) => {
      this.titanRelatedReports = reports;
      if (reports.reports.length === 0 && reports.reportCount > 0) {
        for (let i = 0; i < reports.reportCount; i++) {
          this.loremTexts.push(this.generateLoremWords());
        }
      }
    });
  }

  private isFavorited(): void {
    this.favoriteService
      .checkThreatProfileFavorite(this.uuid)
      .subscribe((favorite: boolean) => {
        this.favorited = favorite;
      });
  }

  private getHuntPackagesByThreatName(threatProfile: ThreatProfile): void {
    const terms = threatProfile.also_known_as;
    terms.push(threatProfile.profile_name);
    this.huntPackageService
      .getRelatedHuntPackages(terms)
      .subscribe((results: Array<HuntPackageRecord>) => {
        this.relatedHuntPackages = results;
      });
  }
  private getDetails(): void {
    this.threatProfileService
      .getThreatProfileByUUID(this.uuid)
      .subscribe((profile: ThreatProfile) => {
        this.profile = profile;
        this.getHuntPackagesByThreatName(this.profile);
        this.tags = gatherTags(this.profile);
        const subInfo = {
          type: 'Profile',
          url: `/threat-profile/${this.uuid}`,
          title: this.profile.profile_name,
        };

        this.bread.setSubSubCrumb(subInfo);
        if (this.profile.mapping.mitre.mitre_techniques_overall) {
          this.profile.mapping.mitre.mitre_techniques_overall.forEach(
            (element) => {
              const mitre = { technique: '', id: '' };

              const mitreSplit = element.split(/[\u2013\u2014-]/); // platform agnostic dash
              mitre.technique = mitreSplit[0];
              mitre.id = mitreSplit[1];
              this.mitres.push(mitre);
            },
          );
        }

        this.trackingService.trackEvent(
          TrackingEventEnum.RESEARCH_THREAT_PROFILE_ACCESSED,
          {
            profile: {
              title: profile.title,
              UUID: profile.UUID,
              mapping: profile.mapping,
              context: profile.context,
            },
          },
        );
      });
  }

  public onAddFavorite(): void {
    this.favoriteService.favoriteThreatProfile(this.uuid).subscribe(() => {
      this.favorited = true;
    });
  }

  public onRemoveFavorite(): void {
    this.favoriteService.unfavoriteThreatProfile(this.uuid).subscribe(() => {
      this.favorited = false;
    });
  }

  public onFeedback(): void {
    this.dialog.open(FeedbackModalComponent, {
      data: {
        uuid: this.profile.UUID,
      },
    });
  }

  public generateLoremWords(): string {
    const numWords = Math.floor(Math.random() * (15 - 8 + 1)) + 8; // Random number between 8 and 15
    return this.lorem.generateWords(numWords);
  }
}
